import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '../../../pipes/translate.pipe';

// 導入類型定義
import { 
  FieldRenderMode, 
  FieldRendererConfig, 
  FormFieldConfig, 
  SupportedFieldType,
  FieldRenderResult 
} from './form-field-renderer.types';

// 導入所有表單元件 (從現有的 total-components 中導入)
import {
  FormTextInputComponent,
  FormSelectComponent,
  FormDatetimeComponent,
  FormPhotoUploadComponent,
  FormTextareaComponent,
  FormRadiobuttonComponent,
  FormNumberInputComponent,
  MultiCheckboxComponent,
  FormDateComponent,
  FormDateRangeComponent,
  FormAvatarUploadComponent,
  FormSignatureComponent,
  FormLocationComponent,
  FormTimestampComponent,
  FormDepartmentSelectorComponent,
  FormPersonnelSelectorComponent,
  FormApprovalPersonnelSelectorComponent,
  FormTagsComponent,
  FormApprovalFlowComponent,
  FormDynamicApiSelectComponent,
  FormDynamicApiTableComponent,
  FormVideoPlayerComponent,
  FormAudioRecorderComponent,
  MeetingSignatureCollectionComponent,
  FillInFieldComponent,
  RiskAssessmentMatrixComponent,
} from '../../items/total-components';

import { FormRichTextDisplayComponent } from '../../items/form-rich-text-display.component';
import { FileDisplayComponent } from '../../items/file-display/file-display.component';
import { FileUploadManagerComponent } from '../../items/file-upload-manager/file-upload-manager.component';
import { FormAutoNumberingComponent } from '../../items/form-auto-numbering/form-auto-numbering.component';

/**
 * 統一的表單字段渲染組件
 * 支援三種渲染模式：FORM (填寫)、DISPLAY (顯示)、LIST (列表)
 */
@Component({
  selector: 'app-form-field-renderer',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslatePipe,
    // 導入所有表單元件
    FormTextInputComponent,
    FormSelectComponent,
    FormDatetimeComponent,
    FormPhotoUploadComponent,
    FormTextareaComponent,
    FormRadiobuttonComponent,
    FormNumberInputComponent,
    MultiCheckboxComponent,
    FormDateComponent,
    FormDateRangeComponent,
    FormAvatarUploadComponent,
    FormSignatureComponent,
    FormLocationComponent,
    FormTimestampComponent,
    FormDepartmentSelectorComponent,
    FormPersonnelSelectorComponent,
    FormApprovalPersonnelSelectorComponent,
    FormTagsComponent,
    FormApprovalFlowComponent,
    FormDynamicApiSelectComponent,
    FormDynamicApiTableComponent,
    FormVideoPlayerComponent,
    FormAudioRecorderComponent,
    MeetingSignatureCollectionComponent,
    FillInFieldComponent,
    RiskAssessmentMatrixComponent,
    FormRichTextDisplayComponent,
    FileDisplayComponent,
    FileUploadManagerComponent,
    FormAutoNumberingComponent
  ],
  templateUrl: './form-field-renderer.component.html',
  styleUrls: ['./form-field-renderer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormFieldRendererComponent implements OnInit, OnChanges {
  
  // 輸入屬性
  @Input() field!: FormFieldConfig;
  @Input() value: any;
  @Input() mode: FieldRenderMode = FieldRenderMode.FORM;
  @Input() formControl?: FormControl;
  @Input() readonly: boolean = false;
  @Input() disabled: boolean = false;
  @Input() preview: boolean = false;
  
  // 額外配置
  @Input() currentUserId?: string;
  @Input() getuseridlist?: boolean;
  @Input() initiatorInfo?: any;
  
  // 輸出事件
  @Output() valueChange = new EventEmitter<any>();
  @Output() fieldClick = new EventEmitter<FormFieldConfig>();
  @Output() validationChange = new EventEmitter<{field: FormFieldConfig, isValid: boolean, errors: string[]}>();
  
  // 枚舉引用 (供模板使用)
  public FieldRenderMode = FieldRenderMode;
  
  // 內部屬性
  public internalFormControl: FormControl = new FormControl();
  public isValidField: boolean = true;
  public fieldErrors: string[] = [];
  
  ngOnInit(): void {
    this.initializeFormControl();
    this.setupValueChangeSubscription();
  }
  
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['field'] || changes['formControl'] || changes['value']) {
      this.initializeFormControl();
    }
    
    if (changes['mode']) {
      this.handleModeChange();
    }
  }
  
  /**
   * 初始化表單控制項
   */
  private initializeFormControl(): void {
    if (this.formControl) {
      this.internalFormControl = this.formControl;
    } else {
      this.internalFormControl = new FormControl(this.value || this.field?.value);
    }
    
    // 設置只讀和禁用狀態
    if (this.readonly || this.disabled || this.mode === FieldRenderMode.DISPLAY || this.mode === FieldRenderMode.LIST) {
      this.internalFormControl.disable();
    } else {
      this.internalFormControl.enable();
    }
  }
  
  /**
   * 設置值變更訂閱
   */
  private setupValueChangeSubscription(): void {
    this.internalFormControl.valueChanges.subscribe(value => {
      this.value = value;
      this.valueChange.emit(value);
      this.validateField();
    });
  }
  
  /**
   * 處理模式變更
   */
  private handleModeChange(): void {
    if (this.mode === FieldRenderMode.DISPLAY || this.mode === FieldRenderMode.LIST) {
      this.internalFormControl.disable();
    } else if (this.mode === FieldRenderMode.FORM && !this.readonly && !this.disabled) {
      this.internalFormControl.enable();
    }
  }
  
  /**
   * 驗證字段
   */
  private validateField(): void {
    this.isValidField = this.internalFormControl.valid;
    this.fieldErrors = this.getFieldErrors();
    
    this.validationChange.emit({
      field: this.field,
      isValid: this.isValidField,
      errors: this.fieldErrors
    });
  }
  
  /**
   * 獲取字段錯誤訊息
   */
  private getFieldErrors(): string[] {
    const errors: string[] = [];
    const controlErrors = this.internalFormControl.errors;
    
    if (controlErrors) {
      if (controlErrors['required']) {
        errors.push('此欄位為必填');
      }
      if (controlErrors['minlength']) {
        errors.push(`最少需要 ${controlErrors['minlength'].requiredLength} 個字符`);
      }
      if (controlErrors['maxlength']) {
        errors.push(`最多只能輸入 ${controlErrors['maxlength'].requiredLength} 個字符`);
      }
      if (controlErrors['pattern']) {
        errors.push('格式不正確');
      }
    }
    
    return errors;
  }
  
  /**
   * 檢查字段類型是否支援
   */
  public isSupportedFieldType(type: string): type is SupportedFieldType {
    const supportedTypes: SupportedFieldType[] = [
      'text-input', 'number-input', 'textarea', 'select', 'radiobutton', 'multi-checkbox',
      'datetime', 'date', 'date-range', 'photo-upload', 'avatar-upload', 'file-upload-manager',
      'signature', 'location', 'approval-flow', 'dynamic-api-select', 'dynamic-api-table',
      'auto-numbering', 'risk-assessment-matrix', 'timestamp', 'department-selector',
      'personnel-selector', 'approval-personnel-selector', 'tags', 'file-display', 'video-player', 'audio-recorder',
      'meeting-signature-collection', 'fill-in-field', 'rich-text-display', 'subform-relation',
      'required-document-upload'
    ];
    
    return supportedTypes.includes(type as SupportedFieldType);
  }
  
  /**
   * 獲取字段顯示值 (用於 DISPLAY 和 LIST 模式)
   */
  public getDisplayValue(): string {
    if (!this.value) {
      return '-';
    }
    
    switch (this.field.type) {
      case 'datetime':
        return this.formatDateTime(this.value);
      case 'date':
        return this.formatDate(this.value);
      case 'select':
      case 'radiobutton':
        return this.getOptionLabel(this.value);
      case 'multi-checkbox':
        return this.getMultiSelectLabels(this.value);
      case 'photo-upload':
        return this.formatPhotoCount(this.value);
      default:
        return String(this.value);
    }
  }
  
  /**
   * 格式化日期時間
   */
  private formatDateTime(value: any): string {
    if (!value) return '-';
    try {
      const date = new Date(value);
      return date.toLocaleString('zh-TW');
    } catch {
      return String(value);
    }
  }
  
  /**
   * 格式化日期
   */
  private formatDate(value: any): string {
    if (!value) return '-';
    try {
      const date = new Date(value);
      return date.toLocaleDateString('zh-TW');
    } catch {
      return String(value);
    }
  }
  
  /**
   * 獲取選項標籤
   */
  private getOptionLabel(value: any): string {
    if (!this.field.options) return String(value);
    const option = this.field.options.find(opt => opt.value === value);
    return option ? option.label : String(value);
  }
  
  /**
   * 獲取多選標籤
   */
  private getMultiSelectLabels(values: any[]): string {
    if (!Array.isArray(values) || !this.field.options) {
      return String(values);
    }
    
    const labels = values.map(value => {
      const option = this.field.options!.find(opt => opt.value === value);
      return option ? option.label : String(value);
    });
    
    return labels.join(', ');
  }
  
  /**
   * 格式化照片數量
   */
  private formatPhotoCount(photos: any): string {
    if (!photos) return '0 張照片';
    if (Array.isArray(photos)) {
      return `${photos.length} 張照片`;
    }
    return '1 張照片';
  }
  
  /**
   * 處理字段點擊事件
   */
  public onFieldClick(): void {
    this.fieldClick.emit(this.field);
  }
  
  /**
   * 獲取字段CSS類別
   */
  public getFieldCssClass(): string {
    const classes = ['form-field-renderer'];
    
    classes.push(`mode-${this.mode}`);
    classes.push(`type-${this.field.type}`);
    
    if (this.readonly) classes.push('readonly');
    if (this.disabled) classes.push('disabled');
    if (!this.isValidField) classes.push('invalid');
    if (this.field.required) classes.push('required');
    
    return classes.join(' ');
  }
  
  /**
   * 檢查是否為顯示模式
   */
  public isDisplayMode(): boolean {
    return this.mode === FieldRenderMode.DISPLAY || this.mode === FieldRenderMode.LIST;
  }
  
  /**
   * 檢查是否為表單模式
   */
  public isFormMode(): boolean {
    return this.mode === FieldRenderMode.FORM;
  }
  
  /**
   * 檢查是否為列表模式
   */
  public isListMode(): boolean {
    return this.mode === FieldRenderMode.LIST;
  }
}